import { Button, Card, Empty, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePaymentsList, type Payment, type PaymentStatus } from '../api/payments';
import { formatUzs } from '@/shared/lib/format';
import { RoleGate, ACCOUNTANT_ROLES } from '@/shared/auth/role';

const STATUS_COLOR: Record<PaymentStatus, string> = {
  pending: 'gold',
  confirmed: 'green',
  reversed: 'red',
};

export function PaymentsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = usePaymentsList();

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('payments.title')}</Typography.Title>
        <RoleGate roles={ACCOUNTANT_ROLES}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/payments/new')}>
            {t('payments.new')}
          </Button>
        </RoleGate>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        {isLoading ? (
          <Skeleton active style={{ padding: 24 }} />
        ) : (data?.data ?? []).length === 0 ? (
          <Empty style={{ padding: 32 }} description={t('payments.empty')} />
        ) : (
          <Table<Payment>
            rowKey="id"
            dataSource={data?.data ?? []}
            pagination={false}
            onRow={(record) => ({ onClick: () => navigate(`/payments/${record.id}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: t('payments.cols.date'), dataIndex: 'paymentDate', width: 110, render: (v: string) => dayjs(v).format('DD.MM.YYYY') },
              { title: t('payments.cols.amount'), width: 160, render: (_: unknown, r: Payment) => formatUzs(r.amount) },
              {
                title: t('payments.contract'),
                width: 190,
                render: (_: unknown, r: Payment) => r.contract
                  ? <a href={`/contracts/${r.contract.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/contracts/${r.contract!.id}`); }}>{r.contract.contractNumber}</a>
                  : '—',
              },
              {
                title: t('customers.title'),
                render: (_: unknown, r: Payment) => r.customer
                  ? `${r.customer.lastName} ${r.customer.firstName}`
                  : '—',
                ellipsis: true,
              },
              { title: t('payments.cols.method'), dataIndex: 'method', width: 150, render: (m: string) => t(`apartments.paymentMethods.${m}`) },
              { title: t('payments.cols.status'), dataIndex: 'status', width: 120, render: (s: PaymentStatus) => <Tag color={STATUS_COLOR[s]}>{t(`payments.status.${s}`)}</Tag> },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}
